import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Select,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import useCustomizedDataEntrySectionsForTeacher from '../../../hooks/useCustomizedDataEntrySectionsForTeacher';

const CustomDataEntrySection = ({
  allSelectedSectionIds = [],
  sectionDocId,
  customSectionAnswers = [],
  onCustomSectionChange,
  user,
  dataPoint,
  dataPointIndex = 0,
}) => {
  const serializeAnswerValue = (question, value) => {
    if (question.questionType === 'TimePicker') {
      if (!value) return '';
      if (moment.isMoment(value)) {
        return value.isValid() ? value.format('hh:mm A') : '';
      }
      if (value instanceof Date) {
        const parsed = moment(value);
        return parsed.isValid() ? parsed.format('hh:mm A') : '';
      }
      if (typeof value === 'string') {
        return value;
      }
      return '';
    }
    return value;
  };

  const parseTimePickerValue = (storedValue) => {
    if (!storedValue) return null;

    const parseFromPrimitive = (value) => {
      if (!value) return null;
      if (value instanceof Date) {
        const parsed = moment(value);
        return parsed.isValid() ? parsed : null;
      }
      if (typeof value === 'string') {
        const parsed = moment(
          value,
          ['HH:mm', 'hh:mm A', moment.ISO_8601],
          true
        );
        return parsed.isValid() ? parsed : null;
      }
      return null;
    };

    if (moment.isMoment(storedValue)) {
      if (typeof storedValue.toDate === 'function') {
        return storedValue;
      }
      return (
        parseFromPrimitive(storedValue._i) ||
        parseFromPrimitive(storedValue._d) ||
        null
      );
    }

    if (storedValue instanceof Date || typeof storedValue === 'string') {
      return parseFromPrimitive(storedValue);
    }

    if (typeof storedValue === 'object') {
      if (storedValue.answer !== undefined) {
        return parseTimePickerValue(storedValue.answer);
      }
      if (storedValue.value !== undefined) {
        return parseTimePickerValue(storedValue.value);
      }
      if (storedValue._i || storedValue._d) {
        return (
          parseFromPrimitive(storedValue._i) ||
          parseFromPrimitive(storedValue._d) ||
          null
        );
      }
    }

    return null;
  };
  const preventNumberScroll = (event) => {
    event.preventDefault();
    event.target.blur();
  };

  // Find the section's answer object in the array, or create a new one if not present
  const sectionAnswer = customSectionAnswers.find(
    (s) => s.docId === sectionDocId
  ) || { docId: sectionDocId, questions: {} };
  const [questionsState, setQuestionsState] = useState(sectionAnswer.questions);

  // Sync local state with prop changes
  useEffect(() => {
    setQuestionsState(sectionAnswer.questions || {});
  }, [sectionAnswer.questions]);

  // eslint-disable-next-line no-unused-vars
  const [error, setError] = useState('');

  // Fetch all custom data entry sections for the school
  const { data: allCustomSections = [], isLoading: isLoadingSections } =
    useCustomizedDataEntrySectionsForTeacher(user?.schoolId);

  // Find the section definition for this docId
  const sectionDef = allCustomSections.find(
    (section) => section.docId === sectionDocId
  );

  // When questionsState changes, update the parent with the new customSectionAnswers array
  // useEffect(() => {
  //   // Update only this section's entry in the array
  //   const updatedAnswers = [
  //     ...customSectionAnswers.filter((s) => s.docId !== sectionDocId),
  //     { docId: sectionDocId, questions: questionsState },
  //   ];
  //   onCustomSectionChange && onCustomSectionChange(updatedAnswers);
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [questionsState]);
  useEffect(() => {
    const formattedQuestions = Array.isArray(questionsState)
      ? questionsState
      : Object.keys(questionsState).map((id) => ({
          id,
          answer: questionsState[id],
        }));

    const updatedAnswers = [
      ...customSectionAnswers.filter((s) => s.docId !== sectionDocId),
      {
        docId: sectionDocId,
        title: sectionDef?.dataEntrySections?.title || sectionDef?.title || '',
        definition:
          sectionDef?.dataEntrySections?.definition ||
          sectionDef?.definition ||
          '',
        // definition: sectionDef?.definition,
        questions: formattedQuestions,
      },
    ];

    onCustomSectionChange && onCustomSectionChange(updatedAnswers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionsState]);

  const getDefaultValue = (questionType) => {
    switch (questionType) {
      case 'Numeric':
        return '';
      case 'MultipleChoice':
        return '';
      case 'MultiSelectDropdown':
        return [];
      case 'TextBox':
        return '';
      case 'TimePicker':
        return null;
      default:
        return '';
    }
  };

  // ✅ Replace it with this new version:
  const handleCustomDataChange = (sectionId, question, value) => {
    const serializedValue = serializeAnswerValue(question, value);

    setQuestionsState((prevQuestions = []) => {
      const currentArray = Array.isArray(prevQuestions)
        ? prevQuestions
        : Object.keys(prevQuestions).map((key) => ({
            id: key,
            answer: prevQuestions[key],
          }));

      // ✅ FIX: explicitly copy only desired fields
      const updated = currentArray.some((q) => q.id === question.id)
        ? currentArray.map((q) =>
            q.id === question.id
              ? {
                  id: question.id,
                  questionName: question.questionName,
                  questionType: question.questionType,
                  options: question.options || [],
                  answer: serializedValue,
                }
              : q
          )
        : [
            ...currentArray,
            {
              id: question.id,
              questionName: question.questionName,
              questionType: question.questionType,
              options: question.options || [],
              answer: serializedValue,
            },
          ];

      return updated;
    });
  };

  // const getQuestionValue = (sectionId, questionId, questionType) => {
  //   if (questionsState && questionsState[questionId] !== undefined) {
  //     return questionsState[questionId];
  //   }
  //   return getDefaultValue(questionType);
  // };
  const getQuestionValue = (sectionId, questionId, questionType) => {
    const getStoredValue = () => {
      if (Array.isArray(questionsState)) {
        const questionObj = questionsState.find((q) => q.id === questionId);
        return questionObj ? questionObj.answer : undefined;
      }

      if (questionsState && questionsState[questionId] !== undefined) {
        return questionsState[questionId];
      }

      return undefined;
    };

    if (questionType === 'TimePicker') {
      const storedValue = getStoredValue();
      return parseTimePickerValue(storedValue) ?? getDefaultValue(questionType);
    }

    if (Array.isArray(questionsState)) {
      const questionObj = questionsState.find((q) => q.id === questionId);
      return questionObj
        ? questionObj.answer ?? getDefaultValue(questionType)
        : getDefaultValue(questionType);
    }

    const storedValue = getStoredValue();
    if (storedValue !== undefined) {
      return storedValue;
    }

    return getDefaultValue(questionType);
  };

  const renderQuestionInput = (question, sectionId) => {
    const value = getQuestionValue(
      sectionId,
      question.id,
      question.questionType
    );

    switch (question.questionType) {
      case 'Numeric':
        return (
          <TextField
            type='number'
            size='small'
            value={value}
            onChange={(e) =>
              handleCustomDataChange(sectionId, question, e.target.value)
            }
       
            inputProps={{
              'aria-label': 'Numeric input',
              inputMode: 'numeric',
              onWheel: preventNumberScroll,
            }}
            margin='dense'
            sx={{
              width: '100px',
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'white',
                '&:hover': {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#6b7280',
                  },
                },
                '&.Mui-focused': {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#6b7280',
                  },
                },
              },
            }}
          />
        );

      case 'MultipleChoice':
        return (
          <FormControl fullWidth size='small'>
            <RadioGroup
              name={`radio-${question.id}`}
              value={value}
              onChange={(e) =>
                handleCustomDataChange(sectionId, question, e.target.value)
              }
              sx={{ flexDirection: 'row', flexWrap: 'wrap', gap: 2 }}
            >
              {question.options?.map((option, optionIndex) => (
                <FormControlLabel
                  key={optionIndex}
                  value={option}
                  control={<Radio size='small' />}
                  label={option}
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      fontSize: '14px',
                      color: 'var(--clr-theme-gray-dark)',
                    },
                  }}
                />
              ))}
            </RadioGroup>
          </FormControl>
        );

      case 'MultiSelectDropdown':
        return (
          <FormControl fullWidth size='small'>
            <Select
              multiple
              value={value}
              onChange={(e) =>
                handleCustomDataChange(sectionId, question, e.target.value)
              }
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} size='small' />
                  ))}
                </Box>
              )}
              className='input-height'
              sx={{
                width: '70%',
                padding: '0',
                backgroundColor: 'var(--clr-mindly-white)',
              }}
            >
              {question.options?.map((option, optionIndex) => (
                <MenuItem key={optionIndex} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case 'TextBox':
        return (
          <TextField
            multiline
            rows={2}
            fullWidth
            size='small'
            value={value}
            onChange={(e) =>
              handleCustomDataChange(sectionId, question, e.target.value)
            }
            placeholder='Enter text'
            className='input-height'
            sx={{ width: '70%', padding: '0' }}
          />
        );

      case 'TimePicker':
        return (
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <TimePicker
              value={value}
              onChange={(newValue) =>
                handleCustomDataChange(sectionId, question, newValue)
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  size='small'
                  className='input-height'
                  sx={{ width: '70%', padding: '0' }}
                />
              )}
            />
          </LocalizationProvider>
        );

      default:
        return (
          <TextField
            fullWidth
            size='small'
            value={value}
            onChange={(e) =>
              handleCustomDataChange(sectionId, question.id, e.target.value)
            }
            placeholder='Enter value'
            className='input-height'
            sx={{ width: '70%', padding: '0' }}
          />
        );
    }
  };

  if (isLoadingSections) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!sectionDef) {
    return null;
  }

  return (
    <Box sx={{ mt: 2 }}>
      {error && (
        <Alert severity='error' sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Box key={sectionDef.docId} sx={{ mb: 2 }}>
        {sectionDef.dataEntrySections?.questions?.map(
          (question, questionIndex) => (
            <Box key={question.id || questionIndex} sx={{ mb: 2 }}>
              {question.questionType === 'MultipleChoice' ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant='body1'>
                    {question.questionName}
                  </Typography>
                  <Box sx={{ ml: 2 }}>
                    {renderQuestionInput(question, sectionDef.docId)}
                  </Box>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Typography
                    variant='body1'
                    sx={{ minWidth: 'fit-content', pt: '6px' }}
                  >
                    {question.questionName}
                  </Typography>
                  <Box sx={{ flex: 1 }}>
                    {renderQuestionInput(question, sectionDef.docId)}
                  </Box>
                </Box>
              )}
            </Box>
          )
        )}
      </Box>
    </Box>
  );
};

export default CustomDataEntrySection;
